import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { AnalyticsBoundVariable, FunnelInsightNodeMetric } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { StatTierChip } from './StatTierChip';
import { formatPct } from '../statTierCopy';
import { PEOPLE_ABSENCE_LABEL, unitNoun, useFunnelUnit } from '../funnelUnit';
import { nodeFlowForUnit } from '../unitMetrics';

/**
 * Spec D D1b (§12.1 N1/N2/N3/N5): the per-node metric card that replaces the
 * 78px corner badge on the analytics canvas. Render-only — every figure is
 * server-computed (drop-off, reach, qualification, money; AC-D1-7 discipline).
 * The money row renders ONLY for native `Paywall` canvas screen types (N5;
 * WebEmbed is excluded by definition).
 */

const HEAT_HERO_COLOR: Record<string, string> = {
  low: '#2e7d32',
  medium: '#ed6c02',
  high: '#d32f2f',
};

export interface AnalyticsNodeCardProps {
  metric: FunnelInsightNodeMetric;
  /** Canvas screen type of the node (money gate: renders money only for 'Paywall'). */
  screenType?: string;
  /**
   * Canvas rework T2 zoom-LOD: 'map' (zoomed out) renders ONLY the big loss
   * number — the leak map readable from fit view; 'inspect' (default) renders
   * the full banner. The threshold lives in the node renderer (D-C4: 0.35).
   */
  lod?: 'map' | 'inspect';
  /** Canvas rework T5: variables bound to this screen (design-time binding). */
  boundVariables?: AnalyticsBoundVariable[];
}

export const AnalyticsNodeCard: React.FC<AnalyticsNodeCardProps> = ({ metric, screenType, lod = 'inspect', boundVariables }) => {
  const { money } = metric;
  // F2 (FT-AC-090): every figure below comes from the page unit's field set —
  // the visits branch reproduces the pre-F2 derivations verbatim; people mode
  // reads the F1/F2 person twins. Absent twins → D7 typed absence, never the
  // journey number relabelled.
  const unit = useFunnelUnit();
  const noun = unitNoun(unit);
  const flow = nodeFlowForUnit(metric, unit);
  const isMap = lod === 'map';
  const showMoney = !isMap && screenType === 'Paywall' && Boolean(money);
  // Pre-answer zone: every cohort member survived this screen BY CONSTRUCTION
  // (they answered later) — a −0% here is survivor bias, not health. Rates,
  // reach and qualification must not render; the visitor count stays true.
  const observability = metric.filterObservability;
  const isPreAnswer = observability === 'pre';
  // FU: the hero shows the TOTAL loss — flow delta (arrived − left) when it
  // exceeds the explicit-exit counter, which misses silent abandonment and
  // renders «−0» on screens the edge labels visibly show losing people.
  const visitors = flow.visitors ?? 0;
  const totalLossCount = flow.lossCount ?? 0;
  const lossRate = flow.lossRate;
  const heatBucket = flow.heatBucket;
  const qualification = flow.qualification;
  const cumulativeReach = flow.cumulativeReach;
  // Map-LOD sizes live in CANVAS space — at the 0.35 threshold a 6.5rem hero
  // still reads ~36px on screen; the inspect hero is ~2.4× the old 1.05rem.
  const heroSize = isMap ? '6.5rem' : '2.5rem';

  return (
    <Box
      data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeCard(metric.screenId)}
      data-heat={heatBucket ?? 'none'}
      data-lod={lod}
      data-filter-observability={observability ?? 'none'}
      sx={{
        bgcolor: isMap ? 'transparent' : 'background.paper',
        border: theme => (isMap ? 'none' : `1px solid ${theme.palette.divider}`),
        borderRadius: 2,
        boxShadow: isMap ? 0 : 1,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        '&:hover': { boxShadow: isMap ? 0 : 3 },
        fontSize: '0.6875rem',
        textAlign: isMap ? 'center' : undefined,
      }}
    >
      <Stack
        direction="row"
        spacing={isMap ? 2 : 1}
        alignItems="baseline"
        justifyContent={isMap ? 'center' : undefined}
        data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeCardHero}
      >
        {!flow.available ? (
          // D7 (FT-AC-087): people mode on a payload without person twins.
          <>
            <Typography component="span" sx={{ fontSize: isMap ? '4.5rem' : heroSize, fontWeight: 700, lineHeight: 1, color: 'text.disabled' }}>
              n/a
            </Typography>
            {!isMap && (
              <Typography component="span" sx={{ fontSize: '0.8125rem' }} color="text.secondary">
                {PEOPLE_ABSENCE_LABEL}
              </Typography>
            )}
          </>
        ) : isPreAnswer ? (
          <>
            <Typography component="span" sx={{ fontSize: isMap ? '4.5rem' : heroSize, fontWeight: 700, lineHeight: 1, color: 'text.disabled' }}>
              {visitors.toLocaleString('en-US')}
            </Typography>
            {!isMap && (
              <Typography component="span" sx={{ fontSize: '0.8125rem' }} color="text.secondary">
                of the cohort passed here
              </Typography>
            )}
          </>
        ) : lossRate !== undefined ? (
          <>
            <Typography component="span" sx={{ fontSize: heroSize, fontWeight: 700, lineHeight: 1, color: HEAT_HERO_COLOR[heatBucket ?? ''] ?? 'text.primary' }}>
              −{formatPct(lossRate)}
            </Typography>
            {!isMap && (
              <Typography component="span" sx={{ fontSize: '0.8125rem' }} color="text.secondary">
                {/* D5: the unit noun rides the visible label, never tooltip-only. */}
                −{totalLossCount.toLocaleString('en-US')} of {visitors.toLocaleString('en-US')} {noun}
              </Typography>
            )}
          </>
        ) : totalLossCount > 0 ? (
          // Count is real even when the rate isn't statistically readable —
          // hiding it behind «—» reads as «no drops at all».
          <>
            <Typography component="span" sx={{ fontSize: isMap ? '4.5rem' : heroSize, fontWeight: 700, lineHeight: 1, color: 'text.primary' }}>
              −{totalLossCount.toLocaleString('en-US')}
            </Typography>
            {!isMap && (
              <Typography component="span" sx={{ fontSize: '0.8125rem' }} color="text.secondary">
                of {visitors.toLocaleString('en-US')} {noun} · rate not readable yet
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography component="span" sx={{ fontSize: heroSize, fontWeight: 700, lineHeight: 1, color: 'text.disabled' }}>—</Typography>
            {!isMap && (
              <Typography component="span" sx={{ fontSize: '0.8125rem' }} color="text.secondary">
                {/* FT-AC-090: «visitors» over journey numbers was the recorded
                    mislabel — the label now names the actual unit. */}
                {visitors.toLocaleString('en-US')} {noun}
              </Typography>
            )}
          </>
        )}
      </Stack>
      {/* Pre-answer / mixed / anchor honesty captions (trait filter active). */}
      {!isMap && observability && (
        <Typography
          variant="caption"
          data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeCardObservability}
          sx={{ display: 'block', mt: 0.25, fontStyle: 'italic', color: 'text.secondary' }}
        >
          {observability === 'pre' && 'before the answer — drop-off not observable for this filter'}
          {observability === 'mixed' && 'partly before the answer — numbers blend both'}
          {observability === 'anchor' && 'answer screen — the filter starts counting here'}
        </Typography>
      )}
      {!isMap && !isPreAnswer && cumulativeReach !== undefined && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeCardReach}
          sx={{ display: 'block', mt: 0.25 }}
        >
          {formatPct(cumulativeReach)} of entrants get here · {noun}
        </Typography>
      )}
      {!isMap && !isPreAnswer && qualification && (
        <Box sx={{ mt: 0.5 }}>
          <StatTierChip qualification={qualification} />
        </Box>
      )}
      {/* T5 (D-C2): bound-variable chips — available variables get the primary
          outline, excluded ones a muted «private» chip (explains why the
          drawer has no answer data instead of hiding the binding). */}
      {!isMap && (boundVariables?.length ?? 0) > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
          {boundVariables!.map((variable) =>
            variable.excludeFromAnalytics ? (
              <Chip
                key={variable.id}
                data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeVariableChip(variable.id)}
                data-variable-privacy="private"
                size="small"
                variant="outlined"
                label={`🔒 ${variable.name} · private`}
                sx={{ color: 'text.disabled', borderColor: 'divider', fontSize: '0.75rem' }}
              />
            ) : (
              <Chip
                key={variable.id}
                data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeVariableChip(variable.id)}
                data-variable-privacy="available"
                size="small"
                variant="outlined"
                color="primary"
                label={`⚙ ${variable.name}`}
                sx={{ fontSize: '0.75rem' }}
              />
            ),
          )}
        </Stack>
      )}
      {showMoney && money && (
        <Box
          data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeCardMoney}
          sx={{
            mt: 0.75,
            pt: 0.75,
            borderTop: theme => `1px dashed ${theme.palette.divider}`,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            rowGap: 0.25,
            columnGap: 1,
          }}
        >
          {unit === 'people' && (
            // D8 (FT-AC-087): the money lane stays journey-grain in F2 and
            // says so visibly — no silent mixing, no fake conversion.
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / 3', fontStyle: 'italic' }}>
              money counts visits
            </Typography>
          )}
          <MoneyCell label="Buy clicks" value={money.buyButtonClicks.toLocaleString('en-US')} />
          <MoneyCell label="Trials" value={money.trialStartedJourneys.toLocaleString('en-US')} />
          <MoneyCell label="Purchases" value={money.purchaseJourneys.toLocaleString('en-US')} />
          <MoneyCell label="Revenue" value={`$${money.purchaseAmount.toLocaleString('en-US')}`} />
          {money.purchaseConversion && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ gridColumn: '1 / 3' }}>
              <Typography variant="caption" color="text.secondary">Purchase conv</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatPct(money.purchaseConversion.p)}
              </Typography>
              <StatTierChip qualification={money.purchaseConversion} />
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

function MoneyCell({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Typography variant="caption" color="text.secondary" component="span">
      {label} <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>{value}</Typography>
    </Typography>
  );
}
